import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { Modal } from "react-bootstrap";
import { useDraggable, useDroppable } from "@dnd-kit/core";
import {
    Home,
    FileEdit,
    HelpCircle,
    Zap,
    LayoutTemplate,
    ChevronRight,
    ChevronDown,
    Plus,
    Pencil,
    Trash2,
    MoreHorizontal,
    FolderClosed,
    Lock,
    FileText,
    Pin,
    Archive,
} from "lucide-react";
import { LucideIcon } from "./icon-registry";
import { expandedFoldersKey } from "./Manuals";
import { useBookmarks, BookmarkDto } from "./useBookmarks";

function readFolderSet(key: string): Set<string> {
    try {
        const raw = localStorage.getItem(key);
        if (!raw) return new Set();
        const arr = JSON.parse(raw);
        return Array.isArray(arr) ? new Set(arr.filter((x: unknown): x is string => typeof x === "string")) : new Set();
    } catch { return new Set(); }
}
function writeFolderSet(key: string, set: Set<string>): void {
    try { localStorage.setItem(key, JSON.stringify(Array.from(set))); } catch { /* ignore */ }
}

interface Category {
    id: string;
    name: string;
    description: string | null;
    color: string | null;
    icon: string | null;
    sortMode: string;
    accessLevel: string;
    allowedRoles: string | null;
    ownerUserIds?: string[];
}

interface SidebarFolder {
    id: string;
    categoryId: string;
    parentFolderId: string | null;
    name: string;
    description: string | null;
    icon: string | null;
    color: string | null;
    sortMode: string;
    isPinned: boolean;
    sortOrder: number;
    createdBy?: string | null;
}

interface SidebarPage {
    id: string;
    categoryId: string;
    folderId: string | null;
    title: string;
    status: string;
    isPinned: boolean;
}

interface Props {
    categories: Category[];
    expandedCategories: Set<string>;
    currentView: any;
    isAdmin: boolean;
    canManage: boolean;
    userId: string | null;
    onNavigate: (view: any) => void;
    onToggleCategory: (catId: string) => void;
    onSelectCategory: (catId: string) => void;
    onCreateCategory: () => void;
    onEditCategory: (cat: Category) => void;
    onDeleteCategory: (cat: Category) => void;
    onCreateFolder: (categoryId: string) => void;
    onEditFolder: (folder: SidebarFolder) => void;
    onDeleteFolder: (folder: SidebarFolder) => void;
    onSelectPage: (pageId: string, categoryId: string) => void;
    onCreatePage: (categoryId: string, folderId: string | null) => void;
    onSelectFolder: (folderId: string, categoryId: string) => void;
    treeVersion: number;
}

interface CategoryTree {
    folders: SidebarFolder[];
    pages: SidebarPage[];
}

export default function Sidebar({
    categories,
    expandedCategories,
    currentView,
    isAdmin,
    canManage,
    userId,
    onNavigate,
    onToggleCategory,
    onSelectCategory,
    onCreateCategory,
    onEditCategory,
    onDeleteCategory,
    onCreateFolder,
    onEditFolder,
    onDeleteFolder,
    onSelectPage,
    onCreatePage,
    onSelectFolder,
    treeVersion,
}: Props) {
    const bookmarks = useBookmarks();
    // Admins manage everything; a non-admin Editor (canManage but not admin) may only
    // edit items they own — categories they own, folders/pages they created.
    const canEditCategory = (cat: Category) =>
        isAdmin || (canManage && !!userId && (cat.ownerUserIds ?? []).includes(userId));
    const canEditFolder = (folder: SidebarFolder) =>
        isAdmin || (canManage && !!userId && folder.createdBy === userId);
    const [menuOpenId, setMenuOpenId] = useState<string | null>(null);
    const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set());
    const hydratedRef = React.useRef(false);
    const [treeData, setTreeData] = useState<Record<string, CategoryTree>>({});
    const [folderPages, setFolderPages] = useState<Record<string, SidebarPage[]>>({});
    const inFlightRef = React.useRef<Set<string>>(new Set());
    // TODO: REMOVE BEFORE PROD — dev-only reset (companion to backend admin/reset endpoint)
    const [resetOpen, setResetOpen] = useState(false);
    const [resetText, setResetText] = useState("");
    const [resetBusy, setResetBusy] = useState(false);

    const [draftsCount, setDraftsCount] = useState<number>(0);
    const [unansweredCount, setUnansweredCount] = useState<number>(0);
    const [pageRequestsCount, setPageRequestsCount] = useState<number>(0);

    useEffect(() => {
        axios.get("/api/manuals/pages/drafts")
            .then(res => {
                if (res.data.success) setDraftsCount(res.data.items.length);
            })
            .catch(() => {});

        // unanswered questions ("asked" = no accepted answer yet)
        axios.get("/api/manuals/questions?filter=asked")
            .then(res => {
                if (res.data.success) setUnansweredCount(res.data.items.length);
            })
            .catch(() => {});

        // open page requests: admins see all; editors see requests on pages they own
        if (canManage) {
            axios.get("/api/manuals/edit-proposals?status=open&take=200")
                .then(res => {
                    if (res.data.success) setPageRequestsCount(res.data.items.length);
                })
                .catch(() => {});
        }
    }, [treeVersion, isAdmin, canManage]);

    const loadCategoryTree = useCallback((catId: string) => {
        return Promise.all([
            axios.get(`/api/manuals/categories/${catId}/folders`),
            axios.get(`/api/manuals/categories/${catId}/pages`),
        ]).then(([fRes, pRes]) => {
            const folders: SidebarFolder[] = fRes.data.success ? fRes.data.items : [];
            const pages: SidebarPage[] = pRes.data.success ? pRes.data.items : [];
            setTreeData(prev => ({ ...prev, [catId]: { folders, pages } }));
        }).catch(() => {});
    }, []);

    const loadFolderPages = useCallback((folderId: string) => {
        return axios.get(`/api/manuals/folders/${folderId}/pages`)
            .then(res => {
                if (res.data.success) {
                    setFolderPages(prev => ({ ...prev, [folderId]: res.data.items }));
                }
            })
            .catch(() => {});
    }, []);

    useEffect(() => {
        expandedCategories.forEach(catId => {
            loadCategoryTree(catId);
        });
    }, [expandedCategories, treeVersion, loadCategoryTree]);

    useEffect(() => {
        expandedFolders.forEach(fId => {
            if (inFlightRef.current.has(fId)) return;
            inFlightRef.current.add(fId);
            loadFolderPages(fId).finally(() => inFlightRef.current.delete(fId));
        });
    }, [expandedFolders, treeVersion, loadFolderPages]);

    useEffect(() => {
        if (!userId || hydratedRef.current) return;
        setExpandedFolders(readFolderSet(expandedFoldersKey(userId)));
        hydratedRef.current = true;
    }, [userId]);

    const toggleFolder = (fId: string) => {
        setExpandedFolders(prev => {
            const next = new Set(prev);
            if (next.has(fId)) next.delete(fId);
            else next.add(fId);
            if (userId) writeFolderSet(expandedFoldersKey(userId), next);
            return next;
        });
    };

    const isActive = (view: string, id?: string) => {
        if (currentView.view === view) {
            if (id && currentView.categoryId) return currentView.categoryId === id;
            if (id && currentView.pageId) return currentView.pageId === id;
            if (id && currentView.folderId) return currentView.folderId === id;
            return !id;
        }
        return false;
    };

    const isPageActive = (pageId: string) =>
        currentView.view === "page" && currentView.pageId === pageId;

    // close menu on outside click
    useEffect(() => {
        if (!menuOpenId) return;
        const handler = () => setMenuOpenId(null);
        document.addEventListener("click", handler);
        return () => document.removeEventListener("click", handler);
    }, [menuOpenId]);

    function renderFolderTree(folders: SidebarFolder[], pages: SidebarPage[], categoryId: string, parentFolderId: string | null, depth: number) {
        const childFolders = folders.filter(f => f.parentFolderId === parentFolderId);
        const childPages = parentFolderId
            ? (folderPages[parentFolderId] || [])
            : pages.filter(p => !p.folderId);

        const pinnedFolders = childFolders.filter(f => f.isPinned);
        const unpinnedFolders = childFolders.filter(f => !f.isPinned);
        const sortedFolders = [...pinnedFolders, ...unpinnedFolders];

        const pinnedPages = childPages.filter(p => p.isPinned);
        const unpinnedPages = childPages.filter(p => !p.isPinned);
        const sortedPages = [...pinnedPages, ...unpinnedPages];

        const indent = 12 + depth * 20;

        return (
            <>
                {sortedFolders.map(folder => {
                    const fExpanded = expandedFolders.has(folder.id);
                    return (
                        <div key={folder.id}>
                            <DraggableFolderRow
                                folder={folder}
                                categoryId={categoryId}
                                style={{ paddingLeft: indent }}
                                onClick={() => onSelectFolder(folder.id, categoryId)}
                            >
                                <button
                                    className="m-sidebar-chevron"
                                    onClick={e => { e.stopPropagation(); toggleFolder(folder.id); }}
                                >
                                    {fExpanded ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
                                </button>
                                <span
                                    className="m-sidebar-cat-icon m-tree-folder-swatch"
                                    style={{ backgroundColor: folder.color || "#94a3b8" }}
                                >
                                    <LucideIcon name={folder.icon || "FolderClosed"} size={12} color="#fff" />
                                </span>
                                <span className="m-tree-name">{folder.name}</span>
                                {folder.isPinned && <Pin size={14} className="m-tree-pin" fill="currentColor" />}
                                {canManage && (
                                    <button
                                        className="m-sidebar-tree-menu"
                                        onClick={e => {
                                            e.stopPropagation();
                                            setMenuOpenId(menuOpenId === folder.id ? null : folder.id);
                                        }}
                                    >
                                        <MoreHorizontal size={13} />
                                    </button>
                                )}
                            </DraggableFolderRow>
                            {canManage && menuOpenId === folder.id && (
                                <div className="m-sidebar-dropdown" style={{ marginLeft: indent + 16 }}>
                                    <button onClick={() => { setMenuOpenId(null); onCreatePage(categoryId, folder.id); }}>
                                        <FileText size={14} /> New page
                                    </button>
                                    {canEditFolder(folder) && (
                                        <button onClick={() => { setMenuOpenId(null); onEditFolder(folder); }}>
                                            <Pencil size={14} /> Edit
                                        </button>
                                    )}
                                    {isAdmin && (
                                        <button className="danger" onClick={() => { setMenuOpenId(null); onDeleteFolder(folder); }}>
                                            <Trash2 size={14} /> Delete
                                        </button>
                                    )}
                                </div>
                            )}
                            {fExpanded && renderFolderTree(folders, pages, categoryId, folder.id, depth + 1)}
                        </div>
                    );
                })}
                {sortedPages.map(page => (
                    <DraggablePageRow
                        key={page.id}
                        page={page}
                        categoryId={categoryId}
                        active={isPageActive(page.id)}
                        style={{ paddingLeft: indent + 18 }}
                        onClick={() => onSelectPage(page.id, categoryId)}
                    >
                        <FileText size={15} className="m-tree-icon page" />
                        <span className="m-tree-name">{page.title}</span>
                        {page.isPinned && <Pin size={14} className="m-tree-pin" fill="currentColor" />}
                    </DraggablePageRow>
                ))}
            </>
        );
    }

    return (
        <div className="m-sidebar">
            <div className="m-sidebar-nav">
                <button
                    className={`m-sidebar-item${isActive("home") ? " active" : ""}`}
                    onClick={() => onNavigate({ view: "home" })}
                >
                    <Home size={18} />
                    <span>Home</span>
                </button>
                <button
                    className={`m-sidebar-item${isActive("drafts") ? " active" : ""}`}
                    onClick={() => onNavigate({ view: "drafts" })}
                >
                    <FileEdit size={18} />
                    <span>Drafts & To-dos</span>
                    {draftsCount > 0 && <span className="m-sidebar-item-count">{draftsCount}</span>}
                </button>
                <button
                    className={`m-sidebar-item${isActive("questions") ? " active" : ""}`}
                    onClick={() => onNavigate({ view: "questions" })}
                >
                    <HelpCircle size={18} />
                    <span>Questions & Answers</span>
                    {unansweredCount > 0 && <span className="m-sidebar-item-count">{unansweredCount}</span>}
                </button>
                {canManage && (
                    <button
                        className={`m-sidebar-item${isActive("km") ? " active" : ""}`}
                        onClick={() => onNavigate({ view: "km" })}
                    >
                        <Zap size={18} />
                        <span>{isAdmin ? "Permission/Access Management" : "Page Requests"}</span>
                        {pageRequestsCount > 0 && <span className="m-sidebar-item-count">{pageRequestsCount}</span>}
                    </button>
                )}
                {isAdmin && (
                    <button
                        className={`m-sidebar-item${isActive("templates") ? " active" : ""}`}
                        onClick={() => onNavigate({ view: "templates" })}
                    >
                        <LayoutTemplate size={18} />
                        <span>Templates</span>
                    </button>
                )}
                {isAdmin && (
                    <button
                        className={`m-sidebar-item${isActive("archive") ? " active" : ""}`}
                        onClick={() => onNavigate({ view: "archive" })}
                    >
                        <Archive size={18} />
                        <span>Archive</span>
                    </button>
                )}
            </div>

            {bookmarks.items.length > 0 && (
                <div className="m-sidebar-section m-sidebar-bookmarks">
                    <div className="m-sidebar-section-header">
                        <span>Your bookmarks</span>
                    </div>
                    <div className="m-sidebar-tree">
                        {bookmarks.items.map(item => {
                            const isActive = item.targetType === "category"
                                ? currentView.view === "category" && (currentView as any).categoryId === item.targetId
                                : currentView.view === "page" && (currentView as any).pageId === item.targetId;
                            return (
                                <button
                                    key={item.id}
                                    className={`m-sidebar-item${isActive ? " active" : ""}`}
                                    onClick={() => {
                                        if (item.targetType === "category") {
                                            onNavigate({ view: "category", categoryId: item.targetId });
                                        } else {
                                            onSelectPage(item.targetId, item.categoryId || "");
                                        }
                                    }}
                                >
                                    {item.targetType === "category" ? (
                                        <span
                                            className="m-sidebar-cat-icon"
                                            style={{ backgroundColor: item.color || "#94a3b8" }}
                                        >
                                            <LucideIcon name={item.icon} size={14} color="#fff" />
                                        </span>
                                    ) : (
                                        <FileText size={14} style={{ flexShrink: 0 }} />
                                    )}
                                    <span className="m-sidebar-cat-name">{item.title}</span>
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}

            <div className="m-sidebar-section">
                <div className="m-sidebar-section-header">
                    <span>Categories ({categories.length})</span>
                    {canManage && (
                        <button
                            className="m-sidebar-add-btn"
                            title="New category"
                            onClick={onCreateCategory}
                        >
                            <Plus size={16} />
                        </button>
                    )}
                </div>

                <div className="m-sidebar-tree">
                    {categories.map(cat => {
                        const expanded = expandedCategories.has(cat.id);
                        const selected = isActive("category", cat.id);
                        const tree = treeData[cat.id];

                        return (
                            <div key={cat.id}>
                                <CategoryDropRow
                                    cat={cat}
                                    className={selected ? "active" : ""}
                                    onClick={() => onSelectCategory(cat.id)}
                                    onContextMenu={(e: React.MouseEvent) => {
                                        if (!canManage) return;
                                        e.preventDefault();
                                        setMenuOpenId(menuOpenId === cat.id ? null : cat.id);
                                    }}
                                >
                                    <button
                                        className="m-sidebar-chevron"
                                        onClick={e => { e.stopPropagation(); onToggleCategory(cat.id); }}
                                    >
                                        {expanded
                                            ? <ChevronDown size={14} />
                                            : <ChevronRight size={14} />
                                        }
                                    </button>
                                    <span
                                        className="m-sidebar-cat-icon"
                                        style={{ backgroundColor: cat.color || "#94a3b8" }}
                                    >
                                        <LucideIcon name={cat.icon} size={14} color="#fff" />
                                    </span>
                                    <span className="m-sidebar-cat-name">{cat.name}</span>
                                    {cat.accessLevel === "restricted" && (
                                        <Lock size={12} className="m-sidebar-lock" />
                                    )}
                                    {canManage && (
                                        <button
                                            className="m-sidebar-cat-menu"
                                            onClick={e => {
                                                e.stopPropagation();
                                                setMenuOpenId(menuOpenId === cat.id ? null : cat.id);
                                            }}
                                        >
                                            <MoreHorizontal size={14} />
                                        </button>
                                    )}
                                </CategoryDropRow>

                                {canManage && menuOpenId === cat.id && (
                                    <div className="m-sidebar-dropdown">
                                        <button onClick={() => { setMenuOpenId(null); onCreatePage(cat.id, null); }}>
                                            <FileText size={14} /> New page
                                        </button>
                                        <button onClick={() => { setMenuOpenId(null); onCreateFolder(cat.id); }}>
                                            <FolderClosed size={14} /> New folder
                                        </button>
                                        {canEditCategory(cat) && (
                                            <button onClick={() => { setMenuOpenId(null); onEditCategory(cat); }}>
                                                <Pencil size={14} /> Edit
                                            </button>
                                        )}
                                        {isAdmin && (
                                            <button className="danger" onClick={() => { setMenuOpenId(null); onDeleteCategory(cat); }}>
                                                <Trash2 size={14} /> Delete
                                            </button>
                                        )}
                                    </div>
                                )}

                                {expanded && tree && renderFolderTree(tree.folders, tree.pages, cat.id, null, 0)}
                            </div>
                        );
                    })}

                    {categories.length === 0 && (
                        <div className="m-sidebar-empty">No categories yet</div>
                    )}
                </div>
            </div>

            {/* TODO: REMOVE BEFORE PROD — dev-only reset hint (hidden during demo recording) */}
            {false && isAdmin && (
                <div className="m-sidebar-dev-reset">
                    <button type="button" onClick={() => { setResetText(""); setResetOpen(true); }}>reset</button>
                </div>
            )}

            {/* TODO: REMOVE BEFORE PROD — dev-only reset modal */}
            <Modal show={resetOpen} onHide={() => !resetBusy && setResetOpen(false)} centered backdropClassName="m-modal-backdrop">
                <Modal.Header closeButton>
                    <Modal.Title style={{ fontSize: 17, fontWeight: 600 }}>Reset all manuals data</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div style={{ marginBottom: 12, fontSize: 14 }}>
                        This deletes <strong>every</strong> category, folder, page, comment, document, and uploaded blob.
                        It cannot be undone. Only runs in local development.
                    </div>
                    <div style={{ fontSize: 13, color: "#64748b", marginBottom: 6 }}>Type <code>RESET</code> to confirm.</div>
                    <input type="text" className="m-input" autoFocus
                        value={resetText} onChange={e => setResetText(e.target.value)} disabled={resetBusy} />
                </Modal.Body>
                <Modal.Footer>
                    <button type="button" className="m-btn m-btn-ghost" disabled={resetBusy} onClick={() => setResetOpen(false)}>Cancel</button>
                    <button type="button" className="m-btn m-btn-primary"
                        disabled={resetBusy || resetText !== "RESET"}
                        style={{ background: "#dc2626", borderColor: "#dc2626" }}
                        onClick={async () => {
                            setResetBusy(true);
                            try {
                                const res = await axios.post("/api/manuals/admin/reset");
                                if (res.data.success) {
                                    const blobMsg = res.data.blobsDeleted < 0 ? "blob wipe failed" : `${res.data.blobsDeleted} blobs`;
                                    toast.success(`Cleared ${res.data.tablesCleared} tables, ${blobMsg}`);
                                    window.location.reload();
                                } else {
                                    toast.error(res.data.message || "Reset failed");
                                    setResetBusy(false);
                                }
                            } catch (e: any) {
                                toast.error(e.response?.data?.message || "Reset failed");
                                setResetBusy(false);
                            }
                        }}>
                        {resetBusy ? "Resetting..." : "Reset everything"}
                    </button>
                </Modal.Footer>
            </Modal>
        </div>
    );
}

function DraggableFolderRow({ folder, categoryId, children, onClick, style }: any) {
    const { setNodeRef, isOver } = useDroppable({
        id: `folder-drop-${folder.id}`,
        data: { type: "folder", folderId: folder.id, categoryId },
    });
    return (
        <div ref={setNodeRef} onClick={onClick} style={style}
            className={`m-sidebar-tree-item${isOver ? " drop-over" : ""}`}>
            {children}
        </div>
    );
}

function DraggablePageRow({ page, categoryId, children, onClick, active, style }: any) {
    const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
        id: page.id,
        data: { type: "page", categoryId, label: page.title },
    });
    return (
        <div ref={setNodeRef} {...attributes} {...listeners} onClick={onClick} style={style}
            className={`m-sidebar-tree-item${active ? " active" : ""}${isDragging ? " dragging" : ""}`}>
            {children}
        </div>
    );
}

function CategoryDropRow({ cat, children, className, onClick, onContextMenu }: any) {
    const { setNodeRef, isOver } = useDroppable({
        id: `cat-root-${cat.id}`,
        data: { type: "category-root", categoryId: cat.id },
    });
    const classes = ["m-sidebar-cat", className, isOver ? "drop-over" : ""].filter(Boolean).join(" ");
    return (
        <div ref={setNodeRef} className={classes} onClick={onClick} onContextMenu={onContextMenu}>
            {children}
        </div>
    );
}
